import "./TestOrderMedicationComponent.scss";
import {FormikProps, FormikValues} from "formik";
import {ITableColumn} from "../../models/table.model";
import CheckboxComponent from "../core/checkbox/CheckboxComponent";
import React, {RefObject, useCallback} from "react";
import HeadingComponent from "../heading/headingComponent";
import CommonService from "../../services/common.service";
import ButtonComponent from "../core/button/ButtonComponent";
import SearchComponent from "../search/SearchComponent";
import TableWrapperV2Component from "../table-components/table-wrapper-v2/TableWrapperV2Component";
import {APIConfig} from "../../../constants";
import TooltipComponent from "../tooltip/tooltipComponent";
import {medicationObject} from "../../models/testOrder.model";

interface TestOrderMedicationComponentProps {
    formikRef: RefObject<FormikProps<FormikValues>>;
    medicationDrawerConfig: { isOpen: boolean, index: number | string | null };
    filter: { filter: string, search: string },
    setFilter: React.Dispatch<React.SetStateAction<{ filter: string, search: string }>>
}

const MODULE_NAME = 'medication-list';

const TestOrderMedicationComponent = (props: TestOrderMedicationComponentProps) => {
    const {formikRef, medicationDrawerConfig, filter, setFilter} = props;
    const medicationColumns: ITableColumn[] = [
        {
            id: 'select',
            label: <></>,
            // hideInColumnPreferences: true,
            isDefaultColumn: true,
            fixed: "left",
            width: '50',
            content: (item: medicationObject) => {
                const selectedMedicationList = formikRef?.current?.values?.sampleDetails?.[medicationDrawerConfig?.index || 0]?.medicationDetails
                return <CheckboxComponent
                    name={"sample_select"}
                    size={"small"}
                    disableRipple={true}
                    checked={selectedMedicationList?.findIndex((sample: any) => sample?.id === item?.id) > -1}
                    onUpdate={(isChecked: boolean) => {
                        handleSelectDeselectMedication(isChecked, item);
                    }}/>
            }
        },
        {
            id: "brandName",
            label: "Brand Name",
            width: 100,
            isDefaultColumn: true, align: "center",
            content: (row: medicationObject) => {
                const value = row?.brandName ? CommonService.capitalizeFirstLetter(row?.brandName) : "-";
                return value?.length > 14 ? <TooltipComponent placement={'top'} arrow={true} tooltip={value}>
                    <div className={'ellipses-text'}>{value}</div>
                </TooltipComponent> : value
            }
        },
        {
            id: "genericName",
            label: "Generic Name",
            width: 120,
            isDefaultColumn: true, align: "left",
            content: (row: medicationObject) => {
                const value = row?.genericName ? CommonService.capitalizeFirstLetter(row?.genericName) : "-";
                return value?.length > 19 ? <TooltipComponent tooltip={value}>
                    <div className={'ellipses-text'}>{value}</div>
                </TooltipComponent> : value
            }
        },
        {
            id: "dosageForm",
            label: "Dosage Form",
            width: 120,
            isDefaultColumn: true, align: "left",
            content: (row: medicationObject) => {
                const value = row?.dosageForm ? CommonService.capitalizeFirstLetter(row?.dosageForm) : "-";
                return value?.length > 10 ? <TooltipComponent tooltip={value}>
                    <div className={'ellipses-text'}>{value}</div>
                </TooltipComponent> : value
            }
        },
        {
            id: "dosage",
            label: "Dosage",
            width: 120,
            isDefaultColumn: true, align: "left",
            content: (row: medicationObject) => {
                const value = row?.dosage || "-";
                return value?.length > 19 ? <TooltipComponent tooltip={value}>
                    <div className={'ellipses-text'}>{value}</div>
                </TooltipComponent> : value
            }
        },
        {
            id: "unit",
            label: "Unit",
            width: 120,
            isDefaultColumn: true, align: "left",
            content: (row: medicationObject) => {
                const value = row?.unit || "-";
                return value?.length > 12 ? <TooltipComponent tooltip={value}>
                    <div className={'ellipses-text'}>{value}</div>
                </TooltipComponent> : value
            }
        },
    ]

    const handleApplyAlphanumericFilter = useCallback((character: string) => {
        setFilter((prev) => {
            return {
                ...prev,
                filter: prev.filter === character ? "" : character
            }
        })
    }, [setFilter])

    const handleClearAllMedication = useCallback(() => {
        try {
            formikRef?.current && formikRef?.current?.setFieldValue(`sampleDetails[${medicationDrawerConfig?.index || 0}].medicationDetails`, [])
            formikRef?.current?.values?.sampleDetails?.[medicationDrawerConfig?.index || 0]?.sample?.forEach((_: any, index: number) => {
                formikRef?.current && formikRef?.current?.setFieldValue(`sampleDetails[${medicationDrawerConfig?.index || 0}].sample[${index}].medicationDetails`, [])
            })
            //clearing drawer filters
            setFilter(() => {
                return {
                    filter: "",
                    search: ""
                }
            })
            CommonService._communications.TableWrapperRefreshSubject.next({
                moduleName: MODULE_NAME
            });
        } catch (error) {
            console.error("Error while clear all icd code")
        }
    }, [formikRef, medicationDrawerConfig?.index, setFilter])

    const handleSelectDeselectMedication = useCallback((isChecked: boolean, medication: any) => {
        try {
            //Values directly changed at sample details level because of array reference
            const currentMedication = formikRef?.current?.values?.sampleDetails?.[medicationDrawerConfig?.index || 0]?.medicationDetails || [];
            if (isChecked) {
                currentMedication?.push(medication);
            } else {
                const medicationId = medication?.id;
                currentMedication?.splice(currentMedication?.findIndex((item: any) => item?.id === medicationId), 1);
            }
            // Setting the selected medication list to sampleDetails> each sample level;
            formikRef?.current?.values?.sampleDetails?.[medicationDrawerConfig?.index || 0]?.sample?.forEach((_: any, index: number) => {
                formikRef?.current && formikRef?.current?.setFieldValue(`sampleDetails[${medicationDrawerConfig?.index || 0}].sample[${index}].medicationDetails`, currentMedication)
            })
        } catch (error) {
            console.error("Error on select/deselect Medication")
        }
    }, [formikRef, medicationDrawerConfig?.index]);

    return (
        <>
            <div className="screen t-form-screen test-order-medication-drawer">
                <div className="drawer-sub-header horizontal-vertical-center-align">
                    <HeadingComponent title={"Select Medication"}/>
                    <div className={'filters-clear-all-button'}
                         onClick={handleClearAllMedication}
                    >
                        Clear all
                    </div>
                </div>
                <div className="t-form t-form-drawer">
                    <div className="t-form-controls-wrapper">
                        <div className='alphanumeric-filter-wrapper mrg-bottom-25'>
                            {
                                CommonService.getAlphanumericArray()?.map((character: string) => {
                                    return <ButtonComponent
                                        className={`alphanumeric-button ${filter.filter === character ? "active" : ""}`}
                                        color={"secondary"}
                                        handleClick={() => handleApplyAlphanumericFilter(character)}
                                        variant={"outlined"}>{character?.toUpperCase()}</ButtonComponent>
                                })
                            }
                        </div>
                        <SearchComponent label={""} placeholder={"Search"} value={filter.search}
                                         onChange={(e) => setFilter((prev) => {
                                             return {
                                                 ...prev,
                                                 search: e
                                             }
                                         })}/>
                        <div className="table-list-container">
                            <TableWrapperV2Component
                                isPaginated={true}
                                columns={medicationColumns}
                                url={APIConfig.GET_MEDICATION_LIST_TABLE.URL}
                                method={APIConfig.GET_MEDICATION_LIST_TABLE.METHOD}
                                extraPayload={filter}
                                isColumnFilterVisible={false}
                                moduleName={MODULE_NAME}
                                // onDataLoad={(data: any) => setData(data)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

};

export default TestOrderMedicationComponent;
